import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * ActingAsContext — stores the "recruiter acting on behalf of client" state.
 * Persists across tabs via localStorage. The actual header attachment is done
 * by the axios interceptor in /app/frontend/src/lib/api.js so every call
 * across the app picks it up automatically.
 *
 * Shape:
 *   { client_id, client_name, client_email, candidate_user_id }
 *
 * Set this only after the recruiter has linked the client to a Resmo account
 * (POST /api/recruiter/clients/{id}/link returns candidate_user_id).
 */
const STORAGE_KEY = "resmo:acting_as_client";
const ID_KEY = "resmo:acting_as_client_id";

const ActingAsContext = createContext({
  actingAs: null,
  setActingAs: () => {},
  clearActingAs: () => {},
});

export function ActingAsProvider({ children }) {
  const [actingAs, setState] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        try { setState(e.newValue ? JSON.parse(e.newValue) : null); }
        catch { setState(null); }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setActingAs = useCallback((client) => {
    if (!client || !client.client_id) {
      setState(null);
      try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(ID_KEY);
      } catch { /* ignore */ }
      return;
    }
    setState(client);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(client));
      window.localStorage.setItem(ID_KEY, client.client_id);
    } catch { /* ignore */ }
  }, []);

  const clearActingAs = useCallback(() => setActingAs(null), [setActingAs]);

  return (
    <ActingAsContext.Provider value={{ actingAs, setActingAs, clearActingAs }}>
      {children}
    </ActingAsContext.Provider>
  );
}

export function useActingAs() {
  return useContext(ActingAsContext);
}
